import {
  PlayerRequestType,
  PlayerRequestInterceptor,
  PlayerResponseInterceptor,
} from '@dtvgo/player-common-utils';
import { normalizeError } from '@dtvgo/shaka-player-utils';

export function onRequestProxy(
  requestType: PlayerRequestType,
  onRequest: PlayerRequestInterceptor,
  method: string,
) {
  return async (request: framework.NetworkRequestInfo) => {
    const { uris, headers, body, allowCrossSiteCredentials } =
      (await onRequest(requestType, {
        uris: request.url ? [request.url] : [],
        headers: request.headers as { [key: string]: string },
        body: request.content,
        allowCrossSiteCredentials: request.withCredentials,
        method,
      })) || {};

    request.url = uris?.[0] ?? request.url;
    request.headers = {
      ...(request.headers as { [key: string]: string }),
      ...headers,
    };
    request.content = (body as Uint8Array | undefined) ?? request.content;
    request.withCredentials =
      allowCrossSiteCredentials ?? request.withCredentials;

    return request;
  };
}

export function onResponseReceivedProxy(
  requestType: PlayerRequestType,
  onResponseReceived: PlayerResponseInterceptor,
) {
  return async (
    data: unknown,
    response: { headers: { [key: string]: string } },
  ) => {
    const { data: newData } =
      (await onResponseReceived(requestType, {
        data,
        headers: response.headers,
        uri: '',
      })) || {};
    return newData || data;
  };
}

export const PlayerState = cast.framework.ui.State;
export type PlayerStateType = framework.ui.State;

interface ShakaErrorData {
  shakaErrorSeverity: number;
  shakaErrorCategory: number;
  shakaErrorCode: number;
  shakaErrorData: unknown[];
  shakaErrorStack: string;
}

export function isShakaErrorData(error: unknown): error is ShakaErrorData {
  return (
    typeof error === 'object' &&
    error !== null &&
    'shakaErrorCode' in error &&
    error.shakaErrorCode !== undefined
  );
}

export function toShakaErrorLike(error: ShakaErrorData) {
  return {
    severity: error.shakaErrorSeverity,
    category: error.shakaErrorCategory,
    code: error.shakaErrorCode,
    data: error.shakaErrorData,
    stack: error.shakaErrorStack,
  };
}

interface WithErrorInCustomData {
  customData: {
    error: unknown;
  };
}

export function hasErrorInCustomData(
  error: unknown,
): error is WithErrorInCustomData {
  return (
    typeof error === 'object' &&
    error !== null &&
    'customData' in error &&
    typeof error.customData === 'object' &&
    error.customData !== null &&
    'error' in error.customData &&
    !!error.customData.error
  );
}

export function parseCastError(error: unknown) {
  if (isShakaErrorData(error)) {
    return normalizeError(toShakaErrorLike(error));
  }
  if (hasErrorInCustomData(error)) {
    return error.customData.error;
  }
  return error;
}

export function addErrorListener(listener: (error: unknown) => void) {
  const handleError = (event: framework.events.ErrorEvent) => {
    if (event.error) {
      listener(parseCastError(event.error));
    }
  };

  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .addEventListener(cast.framework.events.EventType.ERROR, handleError);

  return () => {
    cast.framework.CastReceiverContext.getInstance()
      .getPlayerManager()
      .removeEventListener(cast.framework.events.EventType.ERROR, handleError);
  };
}

export function addEndedListener(listener: () => void) {
  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .addEventListener(cast.framework.events.EventType.ENDED, listener);
}

export function removeEndedListener(listener: () => void) {
  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .removeEventListener(cast.framework.events.EventType.ENDED, listener);
}

export function addMediaFinishedListener(listener: () => void) {
  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .addEventListener(cast.framework.events.EventType.MEDIA_FINISHED, listener);
}

export function removeMediaFinishedListener(listener: () => void) {
  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .removeEventListener(
      cast.framework.events.EventType.MEDIA_FINISHED,
      listener,
    );
}

export function load(loadRequest: framework.messages.LoadRequestData) {
  cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .load(loadRequest)
    .catch((error) => {
      console.error('[cast>lib>utils>loadMedia] load failed', error);
    });
}

export function reload() {
  const mediaInfo = cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .getMediaInformation();

  if (!mediaInfo) return;

  const loadRequestData = new cast.framework.messages.LoadRequestData();
  loadRequestData.media = mediaInfo;

  loadRequestData.customData = {
    ...loadRequestData.customData,
    reload: true,
  };

  load(loadRequestData);
}

function createAddListener(eventType: framework.events.EventType) {
  return (listener: () => void) => {
    const playerManager = getPlayerManager();
    playerManager.addEventListener(eventType, listener);

    return () => {
      playerManager.removeEventListener(eventType, listener);
    };
  };
}

export const addPlayListener = createAddListener(
  cast.framework.events.EventType.PLAY,
);
export const addPlayingListener = createAddListener(
  cast.framework.events.EventType.PLAYING,
);
export const addPauseListener = createAddListener(
  cast.framework.events.EventType.PAUSE,
);

export function addTimeUpdateListener(
  listener: (currentTime: number | undefined) => void,
): () => void {
  const playerManager = getPlayerManager();

  const handleTimeUpdate = (event: framework.events.MediaElementEvent) => {
    listener(event.currentMediaTime);
  };

  playerManager.addEventListener(
    cast.framework.events.EventType.TIME_UPDATE,
    handleTimeUpdate,
  );

  return () => {
    playerManager.removeEventListener(
      cast.framework.events.EventType.TIME_UPDATE,
      handleTimeUpdate,
    );
  };
}

export function stopPlayback() {
  getPlayerManager().stop();
}

export function broadcastCustomMessage(
  namespace: string,
  data: { type: string; [key: string]: unknown },
) {
  cast.framework.CastReceiverContext.getInstance().sendCustomMessage(
    namespace,
    undefined,
    data,
  );
}

export function getPlayerManager() {
  return cast.framework.CastReceiverContext.getInstance().getPlayerManager();
}

export function getLivePlaybackTimestamp() {
  const playerManager = getPlayerManager();
  const currentMediaTime = playerManager.getCurrentTimeSec();

  const currentAbsoluteTime =
    playerManager.getAbsoluteTimeForMediaTime(currentMediaTime);

  return currentAbsoluteTime ? currentAbsoluteTime * 1000 : Date.now();
}

export function getLiveEdgeTimestamp() {
  const playerManager = getPlayerManager();
  const liveEdgeMediaTime = playerManager.getLiveSeekableRange()?.end;

  const liveEdgeAbsoluteTime =
    liveEdgeMediaTime ?
      playerManager.getAbsoluteTimeForMediaTime(liveEdgeMediaTime)
    : undefined;

  return liveEdgeAbsoluteTime ? liveEdgeAbsoluteTime * 1000 : Date.now();
}

const goLiveListeners: (() => void)[] = [];
export function addGoLiveListener(listener: () => void) {
  if (!goLiveListeners.includes(listener)) {
    goLiveListeners.push(listener);
  }

  return () => {
    const position = goLiveListeners.indexOf(listener);
    if (position < 0) return;

    goLiveListeners.splice(position, 1);
  };
}

export function goLive() {
  const playerManager = getPlayerManager();
  const liveEdgeMediaTime = playerManager.getLiveSeekableRange()?.end;
  if (liveEdgeMediaTime === undefined) return;

  playerManager.seek(liveEdgeMediaTime);

  for (const listener of goLiveListeners) {
    listener();
  }
}
